import { LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { MoneyInputController } from 'common/components/moneyInput/MoneyInput';
import { SelectInputController } from 'common/components/selectInput/SelectInput';
import CheckInput from 'common/components/checkInput/CheckInput';
import { Label } from 'common/components/texts/Texts';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect, useRef, CSSProperties } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm, useWatch, useFieldArray, Controller } from 'react-hook-form';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import newGuid from 'common/utils/newGuid';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { IoMoveOutline } from 'react-icons/io5';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { Form } from 'common/components/form/Form';
import { SmallTabs, Tab } from 'common/components/smallTabs/SmallTabs';
import LanguagesService from 'api/languages/LanguagesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import styles from './BudgetGeneralityDefaultScreen.module.scss';

import { BudgetGeneralityDefaultDto, BudgetGeneralityDefaultFieldDto, BudgetGeneralityDefaultTranslationDto } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultDto';
import BudgetGeneralityDefaultsService from 'api/budgetGeneralityDefaults/BudgetGeneralityDefaultsService';
import UnitsService from 'api/units/UnitsService';
import { UnitDto } from 'api/units/models/UnitDto';

// Limite de caracteres da designação e dos nomes dos campos (apenas frontend).
const MAX_TEXT_LENGTH = 250;

function BudgetGeneralityDefaultScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<BudgetGeneralityDefaultDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [languages, setLanguages] = useState<SelectValueLabel[]>([]);
    const [activeTab, setActiveTab] = useState<string>('');

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGET_GENERALITY_DEFAULTS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGET_GENERALITY_DEFAULTS_READ']);

    const url = '/backoffice/budget-generality-defaults';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    const quantityExpression = useWatch({ control: form.control, name: 'quantityExpression' }) ?? '';
    const unitPriceExpression = useWatch({ control: form.control, name: 'unitPriceExpression' }) ?? '';
    const watchedQuantity = useWatch({ control: form.control, name: 'quantity' });
    const watchedUnitPrice = useWatch({ control: form.control, name: 'unitPrice' });

    const { fields: fieldRows, append: appendField, remove: removeField, move: moveField } = useFieldArray({
        control: form.control,
        name: 'fields',
        keyName: '_key',
    });
    const watchedFields = useWatch({ control: form.control, name: 'fields' }) ?? [];

    // Marca updates programáticos do valor (vindos do modal de expressão) para o onChange do
    // MoneyInput os ignorar e não limpar a fórmula recém-guardada. Indexado por fieldKey.
    const skipFieldClearRef = useRef<Record<string, boolean>>({});

    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const onFieldsDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fieldRows.findIndex(f => f.fieldKey === active.id);
        const newIndex = fieldRows.findIndex(f => f.fieldKey === over.id);
        if (oldIndex >= 0 && newIndex >= 0) {
            moveField(oldIndex, newIndex);
        }
    };

    // No backoffice o formulário pode referenciar campos genéricos do orçamento (totais, IVA padrão)
    // e os próprios campos da generalidade (self|quantity / self|unitPrice). Os valores dos orçamento
    // são placeholders (0) — em runtime no orçamento os mesmos IDs são populados com valores reais.
    const buildExpressionParameters = (excludeField?: 'quantity' | 'unitPrice'): ExpressionParameter[] => {
        const params: ExpressionParameter[] = [];

        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';
        const budgetEntries: Array<[string, string]> = [
            ['totalNetExcludingTax', t('budgets.budget.lines.totals.total_net_without_tax')],
            ['linesTotalNetExcludingTax', t('budgets.budget.lines.totals.total_lines_without_tax')],
            ['totalExcludingTax', t('budgets.budget.lines.totals.total_without_tax')],
            ['totalTax', t('budgets.budget.lines.totals.total_tax')],
            ['totalIncludingTax', t('budgets.budget.lines.totals.total_with_tax')],
            ['defaultTaxValue', t('budgets.budget.default_tax')],
            ['defaultMarginPercentage', t('budgets.budget.default_margin')],
            ['defaultCommissionMarginPercentage', t('budgets.budget.default_commission_margin')],
            ['defaultCombinedMarginPercentage', t('budgets.budget.default_combined_margin')],
        ];
        for (const [id, name] of budgetEntries) {
            params.push({
                id: ExpressionSystem.formatParameterId([budgetKey, id]),
                name: ExpressionSystem.formatParameterName([budgetName, name]),
                value: 0,
            });
        }

        const generalityName = t('budgets.budget.tab_generalities');
        const currentLineLabel = t('budgets.budget.generalities.current_line');
        const selfEntries: Array<['quantity' | 'unitPrice', string, number | null | undefined]> = [
            ['quantity', t('screens.budget_generality_defaults.header.quantity'), watchedQuantity ?? null],
            ['unitPrice', t('screens.budget_generality_defaults.header.unit_price'), watchedUnitPrice ?? 0],
        ];
        for (const [field, name, value] of selfEntries) {
            if (field === excludeField) continue;
            params.push({
                id: ExpressionSystem.formatParameterId(['self', field]),
                name: ExpressionSystem.formatParameterName([generalityName, currentLineLabel, name]),
                value: value ?? 0,
            });
        }

        // Campos personalizados da própria generalidade (placeholders 0 — em runtime no orçamento
        // os mesmos IDs ficam com valores reais).
        for (const fld of watchedFields ?? []) {
            params.push({
                id: ExpressionSystem.formatParameterId(['self', 'field', fld.fieldKey]),
                name: ExpressionSystem.formatParameterName([generalityName, currentLineLabel, fieldDisplayName(fld)]),
                value: 0,
            });
        }

        return params;
    };

    // Parâmetros disponíveis na fórmula de um campo: totais do orçamento (placeholders) + os outros
    // campos da mesma generalidade (excluindo o próprio).
    const buildFieldExpressionParameters = (excludeFieldKey: string): ExpressionParameter[] => {
        const params: ExpressionParameter[] = [];

        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';
        const budgetEntries: Array<[string, string]> = [
            ['totalNetExcludingTax', t('budgets.budget.lines.totals.total_net_without_tax')],
            ['linesTotalNetExcludingTax', t('budgets.budget.lines.totals.total_lines_without_tax')],
            ['totalExcludingTax', t('budgets.budget.lines.totals.total_without_tax')],
            ['totalTax', t('budgets.budget.lines.totals.total_tax')],
            ['totalIncludingTax', t('budgets.budget.lines.totals.total_with_tax')],
            ['defaultTaxValue', t('budgets.budget.default_tax')],
            ['defaultMarginPercentage', t('budgets.budget.default_margin')],
            ['defaultCommissionMarginPercentage', t('budgets.budget.default_commission_margin')],
            ['defaultCombinedMarginPercentage', t('budgets.budget.default_combined_margin')],
        ];
        for (const [id, name] of budgetEntries) {
            params.push({
                id: ExpressionSystem.formatParameterId([budgetKey, id]),
                name: ExpressionSystem.formatParameterName([budgetName, name]),
                value: 0,
            });
        }

        const generalityName = t('budgets.budget.tab_generalities');
        const currentLineLabel = t('budgets.budget.generalities.current_line');

        // Quantidade/preço da própria generalidade.
        const selfEntries: Array<['quantity' | 'unitPrice', string, number | null | undefined]> = [
            ['quantity', t('screens.budget_generality_defaults.header.quantity'), watchedQuantity ?? null],
            ['unitPrice', t('screens.budget_generality_defaults.header.unit_price'), watchedUnitPrice ?? 0],
        ];
        for (const [id, name, value] of selfEntries) {
            params.push({
                id: ExpressionSystem.formatParameterId(['self', id]),
                name: ExpressionSystem.formatParameterName([generalityName, currentLineLabel, name]),
                value: value ?? 0,
            });
        }

        for (const fld of watchedFields ?? []) {
            if (fld.fieldKey === excludeFieldKey) continue;
            params.push({
                id: ExpressionSystem.formatParameterId(['self', 'field', fld.fieldKey]),
                name: ExpressionSystem.formatParameterName([generalityName, currentLineLabel, fieldDisplayName(fld)]),
                value: 0,
            });
        }

        return params;
    };

    const fieldDisplayName = (fld: BudgetGeneralityDefaultFieldDto): string => {
        const tr = fld.translations?.find(x => (x.designation ?? '').trim() !== '');
        return tr?.designation ?? '';
    };

    useEffect(() => {
        void getData();
    }, [id, type]);

    const buildEmptyTranslations = (isos: string[]): BudgetGeneralityDefaultTranslationDto[] => {
        return isos.map(languageIso => ({ languageIso, designation: '' }));
    };

    const getData = async () => {
        try {
            Loading.show();
            const [unitsData, langs] = await Promise.all([
                UnitsService.getFormattedCatalog(),
                LanguagesService.GetAllForSelectItem(),
            ]);
            setUnits(unitsData);
            setLanguages(langs);
            const isos = langs.map(l => l.label ?? '').filter(Boolean);
            const firstIso = isos[0] ?? '';
            setActiveTab(prev => prev && isos.includes(prev) ? prev : firstIso);

            let result: Partial<BudgetGeneralityDefaultDto> = {
                id,
                order: 0,
                unitPrice: 0,
                translations: buildEmptyTranslations(isos),
                fields: [],
            };

            if (id) {
                const loaded = await BudgetGeneralityDefaultsService.getById(id);
                const translations = isos.map(iso => {
                    const existing = loaded.translations?.find(x => x.languageIso === iso);
                    return existing ?? { languageIso: iso, designation: '' };
                });
                const fields = (loaded.fields ?? [])
                    .slice()
                    .sort((a, b) => a.position - b.position)
                    .map(f => ({
                        ...f,
                        translations: isos.map(iso => {
                            const existing = f.translations?.find(x => x.languageIso === iso);
                            return existing ?? { languageIso: iso, designation: '' };
                        }),
                    }));
                result = { ...loaded, translations, fields };
            }
            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budget generality default', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, navId?: string) => {
        if (typeUrl) {
            navigate(`${url}/${typeUrl}/${navId ?? ''}`);
        } else {
            navigate(url);
        }
    };

    const onSubmit = async (model: BudgetGeneralityDefaultDto) => {
        const isos = languages.map(l => l.label ?? '').filter(Boolean);
        const missingIndex = isos.findIndex(iso => {
            const tr = model.translations?.find(t => t.languageIso === iso);
            return !tr || !(tr.designation ?? '').trim();
        });

        if (missingIndex >= 0) {
            const missingIso = isos[missingIndex];
            form.setError(`translations.${missingIndex}.designation` as const, { type: 'required' });
            setActiveTab(missingIso);
            Toast.warning(t('messages.required_fields_empty'));
            return;
        }

        // Designação limitada a MAX_TEXT_LENGTH em qualquer língua. Validado aqui (e não só pela regra
        // inline) porque as tabs de língua são lazy-mounted: só a língua ativa está montada/validada.
        const tooLongIndex = isos.findIndex(iso => {
            const tr = model.translations?.find(t => t.languageIso === iso);
            return (tr?.designation?.length ?? 0) > MAX_TEXT_LENGTH;
        });
        if (tooLongIndex >= 0) {
            form.setError(`translations.${tooLongIndex}.designation` as const, { type: 'maxLength' });
            setActiveTab(isos[tooLongIndex]);
            Toast.warning(t('common.errors.max_length_detail', { maxLength: MAX_TEXT_LENGTH }));
            return;
        }

        // Cada campo personalizado tem de ter nome preenchido em todas as línguas.
        for (let fIdx = 0; fIdx < (model.fields?.length ?? 0); fIdx++) {
            const field = model.fields[fIdx];
            const missingLangIdx = isos.findIndex(iso => {
                const tr = field.translations?.find(t => t.languageIso === iso);
                return !tr || !(tr.designation ?? '').trim();
            });
            if (missingLangIdx >= 0) {
                form.setError(`fields.${fIdx}.translations.${missingLangIdx}.designation` as const, { type: 'required' });
                setActiveTab(isos[missingLangIdx]);
                Toast.warning(t('messages.required_fields_empty'));
                return;
            }
            const longLangIdx = isos.findIndex(iso => {
                const tr = field.translations?.find(t => t.languageIso === iso);
                return (tr?.designation?.length ?? 0) > MAX_TEXT_LENGTH;
            });
            if (longLangIdx >= 0) {
                form.setError(`fields.${fIdx}.translations.${longLangIdx}.designation` as const, { type: 'maxLength' });
                setActiveTab(isos[longLangIdx]);
                Toast.warning(t('common.errors.max_length_detail', { maxLength: MAX_TEXT_LENGTH }));
                return;
            }
        }

        try {
            Loading.show();
            const translations = isos.map(iso => model.translations.find(t => t.languageIso === iso) ?? { languageIso: iso, designation: '' });
            const fields = (model.fields ?? []).map((f, idx) => ({
                ...f,
                position: idx,
                translations: isos.map(iso => f.translations?.find(t => t.languageIso === iso) ?? { languageIso: iso, designation: '' }),
            }));
            const payload: BudgetGeneralityDefaultDto = { ...model, translations, fields };

            if (model && model.id) {
                await BudgetGeneralityDefaultsService.update(payload);
                void getData();
            } else if (model) {
                const newId = await BudgetGeneralityDefaultsService.create(payload);
                navigateTo('edit', newId);
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't save the budget generality default with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));
            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }
        Loading.show();
        try {
            await BudgetGeneralityDefaultsService.remove(form.getValues());
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget generality default', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteModal(false);
        }
    };

    const unitOptions = units.map(u => ({ value: u.id, label: u.name }));
    const activeLangIdx = Math.max(0, languages.findIndex(l => (l.label ?? '') === activeTab));

    const onAddField = () => {
        appendField({
            id: undefined,
            position: fieldRows.length,
            fieldKey: newGuid(),
            value: 0,
            valueExpression: null,
            unitId: null,
            unitSymbol: null,
            showInPdf: false,
            translations: languages.map(l => ({ languageIso: l.label ?? '', designation: '' })),
        });
    };

    const languageName = (iso: string): string => {
        const fallback = languages.find(l => l.label === iso)?.fullLabel ?? iso;
        const key = `languages.${iso}`;
        const translated = t(key);
        return translated === key ? fallback : translated;
    };

    const firstTranslationWithValue = form.watch('translations')?.find(tr => (tr.designation ?? '').trim() !== '');
    const entityName = firstTranslationWithValue?.designation ?? '';
    const titlePrefix = type === 'create'
        ? t('common.new_title.budget_generality_default')
        : type === 'edit'
            ? t('common.edit_title.budget_generality_default')
            : t('common.details_title.budget_generality_default');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('budget_generality_defaults.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budget_generality_defaults.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    {languages.length > 0 && activeTab && (
                        <div className={styles.translationsTabs}>
                            <SmallTabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                {languages.map((lang, idx) => {
                                    const iso = lang.label ?? '';
                                    const translationError = (errors as any).translations?.[idx]?.designation;
                                    return (
                                        <Tab key={iso || idx} eventKey={iso} title={languageName(iso)}>
                                            <Row className="mb-3 mt-3">
                                                <Col>
                                                    <Label space>{t('screens.budget_generality_defaults.header.designation')}{!isDetails ? '*' : ''}</Label>
                                                    <TextInputController
                                                        name={`translations.${idx}.designation` as const}
                                                        control={form.control}
                                                        placeholder={t('screens.budget_generality_defaults.header.designation')}
                                                        disabled={isDetails}
                                                        rules={{ required: true, maxLength: 250 }}
                                                        hasError={Boolean(translationError)}
                                                    />
                                                    <InputError error={translationError} maxLength={250} />
                                                </Col>
                                            </Row>
                                        </Tab>
                                    );
                                })}
                            </SmallTabs>
                        </div>
                    )}
                    <Row className="mb-3 mt-3">
                        <Col xs={6} md={3}>
                            <Label space>{t('screens.budget_generality_defaults.header.order')}{!isDetails ? '*' : ''}</Label>
                            <MoneyInputController
                                name='order'
                                control={form.control}
                                disabled={isDetails}
                                decimalScale={0}
                                hideUnitsDrop
                                rules={{ required: true }}
                                hasError={Boolean((errors as any).order)}
                            />
                            <InputError error={(errors as any).order} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Label space>{t('screens.budget_generality_defaults.header.quantity')}</Label>
                            <MoneyInputController
                                name='quantity'
                                control={form.control}
                                disabled={isDetails}
                                decimalScale={4}
                                hideUnitsDrop
                                expressionButton={{
                                    title: t('screens.budget_generality_defaults.header.quantity'),
                                    value: quantityExpression,
                                    loadParameters: () => buildExpressionParameters('quantity'),
                                    onChange: (expr, value) => {
                                        form.setValue('quantityExpression', expr);
                                        form.setValue('quantity', value ?? null);
                                    },
                                }}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <Label space>{t('screens.budget_generality_defaults.header.unit')}{!isDetails ? '*' : ''}</Label>
                            <SelectInputController
                                name='unitId'
                                control={form.control}
                                options={unitOptions}
                                disabled={isDetails}
                                rules={{ required: true }}
                                hasError={Boolean((errors as any).unitId)}
                            />
                            <InputError error={(errors as any).unitId} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Label space>{t('screens.budget_generality_defaults.header.unit_price')}{!isDetails ? '*' : ''}</Label>
                            <MoneyInputController
                                name='unitPrice'
                                control={form.control}
                                disabled={isDetails}
                                unitPrefix='€'
                                hideUnitsDrop
                                rules={{ required: true }}
                                hasError={Boolean((errors as any).unitPrice)}
                                expressionButton={{
                                    title: t('screens.budget_generality_defaults.header.unit_price'),
                                    value: unitPriceExpression,
                                    loadParameters: () => buildExpressionParameters('unitPrice'),
                                    onChange: (expr, value) => {
                                        form.setValue('unitPriceExpression', expr);
                                        form.setValue('unitPrice', value ?? 0);
                                    },
                                }}
                            />
                            <InputError error={(errors as any).unitPrice} />
                        </Col>
                    </Row>

                    {(!isDetails || fieldRows.length > 0) && (
                        <>
                            <hr />
                            <Row className="mb-2 mt-3">
                                <Col>
                                    <Label space>{t('screens.budget_generality_defaults.fields.title')}</Label>
                                </Col>
                            </Row>
                            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onFieldsDragEnd}>
                                <SortableContext items={fieldRows.map(f => f.fieldKey)} strategy={verticalListSortingStrategy}>
                                    {fieldRows.map((fieldRow, idx) => {
                                        const fieldKey = watchedFields[idx]?.fieldKey ?? fieldRow.fieldKey;
                                        return (
                                            <SortableFieldRow key={fieldRow._key} id={fieldRow.fieldKey} disabled={isDetails}>
                                                {(handleProps) => (
                                                    <Row className="mb-3">
                                                        <Col xs={12} md={6} xxl={3}>
                                                            <Label space>{t('screens.budget_generality_defaults.fields.name')}{!isDetails ? '*' : ''}</Label>
                                                            <TextInputController
                                                                key={`field-name-${idx}-${activeTab}`}
                                                                name={`fields.${idx}.translations.${activeLangIdx}.designation` as const}
                                                                control={form.control}
                                                                placeholder={t('screens.budget_generality_defaults.fields.name')}
                                                                disabled={isDetails}
                                                                rules={{ required: true, maxLength: 250 }}
                                                                hasError={Boolean((errors as any).fields?.[idx]?.translations?.[activeLangIdx]?.designation)}
                                                            />
                                                            <InputError error={(errors as any).fields?.[idx]?.translations?.[activeLangIdx]?.designation} maxLength={250} />
                                                        </Col>
                                                        <Col xs={12} md={6} xxl={3}>
                                                            <Label space>{t('screens.budget_generality_defaults.fields.value')}</Label>
                                                            <MoneyInputController
                                                                name={`fields.${idx}.value` as const}
                                                                control={form.control}
                                                                disabled={isDetails}
                                                                decimalScale={4}
                                                                hideUnitsDrop
                                                                onChange={() => {
                                                                    // Escrever diretamente um valor limpa a fórmula do campo
                                                                    // (ignora updates programáticos vindos do modal de expressão).
                                                                    if (skipFieldClearRef.current[fieldKey]) {
                                                                        skipFieldClearRef.current[fieldKey] = false;
                                                                        return;
                                                                    }
                                                                    if (form.getValues(`fields.${idx}.valueExpression` as const)) {
                                                                        form.setValue(`fields.${idx}.valueExpression` as const, null);
                                                                    }
                                                                }}
                                                                expressionButton={{
                                                                    title: fieldDisplayName(watchedFields[idx] ?? fieldRow) || t('screens.budget_generality_defaults.fields.value'),
                                                                    value: watchedFields[idx]?.valueExpression ?? '',
                                                                    loadParameters: () => buildFieldExpressionParameters(fieldKey),
                                                                    onChange: (expr, value) => {
                                                                        skipFieldClearRef.current[fieldKey] = true;
                                                                        form.setValue(`fields.${idx}.valueExpression` as const, expr);
                                                                        form.setValue(`fields.${idx}.value` as const, value ?? 0);
                                                                    },
                                                                }}
                                                            />
                                                        </Col>
                                                        <Col xs={12} md={6} xxl={3}>
                                                            <Label space>{t('screens.budget_generality_defaults.fields.unit')}</Label>
                                                            <SelectInputController
                                                                name={`fields.${idx}.unitId` as const}
                                                                control={form.control}
                                                                options={unitOptions}
                                                                disabled={isDetails}
                                                            />
                                                        </Col>
                                                        <Col xs={6} md={6} xxl={2} className="d-flex flex-column">
                                                            <Label space>{t('screens.budget_generality_defaults.fields.show_in_pdf')}</Label>
                                                            <Controller
                                                                control={form.control}
                                                                name={`fields.${idx}.showInPdf` as const}
                                                                render={({ field: f }) => (
                                                                    <CheckInput
                                                                        checked={!!f.value}
                                                                        onChange={e => f.onChange(e.target.checked)}
                                                                        disabled={isDetails}
                                                                    />
                                                                )}
                                                            />
                                                        </Col>
                                                        {!isDetails && (
                                                            <Col xs={6} md={6} xxl={1} className="d-flex align-items-end gap-2">
                                                                <Button variant='third' className="px-2" {...handleProps} title={t('common.move')}>
                                                                    <IoMoveOutline />
                                                                </Button>
                                                                <Button variant='danger' onClick={() => removeField(idx)}>
                                                                    <FaTrash />
                                                                </Button>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                )}
                                            </SortableFieldRow>
                                        );
                                    })}
                                </SortableContext>
                            </DndContext>
                            {!isDetails && (
                                <Row className="mb-3">
                                    <Col>
                                        <Button onClick={onAddField}>
                                            <FaPlus /> {t('screens.budget_generality_defaults.fields.add')}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </PageContainer>
            </Form>
            <ConfirmDeleteModal
                itemName={entityName}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

interface SortableFieldRowProps {
    id: string;
    disabled?: boolean;
    children: (handleProps: Record<string, unknown>) => JSX.Element;
}

function SortableFieldRow({ id, disabled, children }: SortableFieldRowProps): JSX.Element {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style: CSSProperties = {
        // Lista vertical: anula o deslocamento horizontal para a linha só se mover para cima/baixo.
        transform: transform ? CSS.Translate.toString({ ...transform, x: 0 }) : undefined,
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    return (
        <div ref={setNodeRef} style={style}>
            {children({ ...attributes, ...listeners })}
        </div>
    );
}

export default BudgetGeneralityDefaultScreen;
